import { Button } from "@newsela/angelou";
import { actionNames, objectTypes, withTrackEvent } from "@newsela/snitch";
import { useEffect } from "react";

import {
  subtitleCss,
  titleCss,
} from "@/search/Langston/FilterPanel/filters/Standards/StandardsModal/shared/StandardsSetsHeader/StandardsSetsHeader.styles";
import { useStandardsModalContext } from "@/search/Langston/FilterPanel/filters/Standards/StandardsModalProvider";
import { IStandardsSetListViewModel } from "@/search/Langston/types/IStandardsSetListViewModel";
import { STANDARDS_SET_MODE } from "@/search/Langston/types/IStandardsSetMode";
import { SelectedStandardsInSet } from "../../shared/SelectedStandardsInSet";
import { StandardsList } from "../../shared/StandardsList/StandardsList";
import { StandardsSetsHeader } from "../../shared/StandardsSetsHeader";
import { StandardsSetsTitleInput } from "../../shared/StandardsSetsTitleInput";

import {
  editableHeaderCss,
  editButtonCss,
  filtersCss,
  headerContainerCss,
  inputAndFiltersCss,
  selectedChipsCss,
  titleAndSubtitleContainer,
  titleInputContainerCss,
} from "./EditableStandardsSet.styles";

const ButtonWithTrackEvent = withTrackEvent(Button);

interface EditableStandardsSetProps {
  standardsSet: IStandardsSetListViewModel;
  limit?:
    | {
        count: number;
        temporarySelectedIds: string[];
        setTemporarySelectedIds: (ids: string[]) => void;
      }
    | undefined;
  canCreateOrEdit?: boolean;
}

export const EditableStandardsSet = ({
  standardsSet,
  limit,
  canCreateOrEdit = true,
}: EditableStandardsSetProps) => {
  const {
    getters: { isEditingSet, standardsSetMode },
    handlers: { setStandardsSetMode, setIsEditingSet },
  } = useStandardsModalContext();

  const isEditMode = standardsSetMode === STANDARDS_SET_MODE.EDIT;

  const toggleEditMode = () => {
    setStandardsSetMode(STANDARDS_SET_MODE.EDIT);
    setIsEditingSet(true);
  };

  useEffect(() => {
    if (isEditingSet) setStandardsSetMode("edit");

    if (!isEditingSet) setStandardsSetMode("view");
  }, [isEditingSet]);

  if (!standardsSet.children) return null;

  return (
    <>
      {!isEditMode ? (
        <div css={editableHeaderCss}>
          <div css={titleAndSubtitleContainer}>
            {<p css={titleCss}>{standardsSet.title}</p>}
            <span css={subtitleCss}>
              {
                <span>
                  Select standards to find results that fit your needs
                </span>
              }
            </span>
          </div>
          {canCreateOrEdit ? (
            <ButtonWithTrackEvent
              size={Button.sizes.lg}
              flavor={Button.flavors.secondary}
              onClick={toggleEditMode}
              __cssFor={editButtonCss}
              objectType={objectTypes.content}
              actionName={actionNames.update}
              streamProperties={{
                description: "Edit standard set",
              }}
            >
              Edit set
            </ButtonWithTrackEvent>
          ) : null}
        </div>
      ) : null}
      {isEditMode ? (
        <>
          <div css={headerContainerCss}>
            <div css={inputAndFiltersCss}>
              <div css={titleInputContainerCss}>
                <StandardsSetsTitleInput />
              </div>
              <div css={filtersCss}>
                <StandardsSetsHeader />
              </div>
            </div>
          </div>
          <div css={selectedChipsCss}>
            {!!standardsSet.children.length && <SelectedStandardsInSet />}
          </div>
          <StandardsList limit={limit} />
        </>
      ) : (
        <StandardsList limit={limit} standards={standardsSet.children} />
      )}
    </>
  );
};
